import { useEffect, useState } from "react";
import api, { errMsg } from "@/lib/api";
import { formatAmount, formatEventDate } from "@/lib/format";
import ZButton from "@/components/ZButton";
import { ZInput, ZSelect, ZTextarea } from "@/components/ZInput";
import Modal from "@/components/Modal";
import EmptyState from "@/components/EmptyState";
import { Wallet, FileText, CheckCircle2, Pencil } from "lucide-react";

const TABS = [
  { id: "to_pay", label: "À reverser" },
  { id: "to_invoice", label: "À facturer" },
  { id: "history", label: "Historique" },
];

export default function AdminPayoutsPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState(null);
  const [tab, setTab] = useState("to_pay");
  const [open, setOpen] = useState(null);
  const [form, setForm] = useState({ amount: "", method: "", reference: "", note: "" });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [commEdit, setCommEdit] = useState(null); // { event_id, value }

  const load = () => {
    setLoading(true);
    Promise.all([
      api.get("/admin/payouts").then((r) => setItems(r.data)),
      api.get("/admin/settings").then((r) => setSettings(r.data)),
    ]).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const openPayout = (it) => {
    setOpen({ kind: "payout", item: it });
    setForm({ amount: String(it.net_payout || 0), method: settings?.payout_methods?.[0] || "", reference: "", note: "" });
    setErr("");
  };
  const openInvoice = (it) => {
    setOpen({ kind: "invoice", item: it });
    setForm({ amount: String(it.invoice_amount || 0), method: settings?.payout_methods?.[0] || "", reference: "", note: "" });
    setErr("");
  };

  const submit = async () => {
    setErr("");
    if (!form.method || !form.amount) return setErr("Méthode et montant obligatoires.");
    setBusy(true);
    try {
      const url = open.kind === "payout"
        ? `/admin/payouts/event/${open.item.event_id}/payout`
        : `/admin/payouts/event/${open.item.event_id}/invoice`;
      await api.post(url, { amount: parseFloat(form.amount), method: form.method, reference: form.reference, note: form.note });
      setOpen(null);
      load();
    } catch (e) {
      setErr(errMsg(e));
    } finally { setBusy(false); }
  };

  const saveCommission = async (clear = false) => {
    if (!commEdit) return;
    setBusy(true);
    try {
      const value = clear ? null : (parseFloat(commEdit.value) || 0);
      await api.patch(`/admin/events/${commEdit.event_id}/economics`, { commission_pct: value });
      setCommEdit(null);
      load();
    } catch (e) {
      alert(errMsg(e));
    } finally { setBusy(false); }
  };

  // Tab filtering
  let view = items;
  if (tab === "to_pay") view = items.filter((it) => it.is_paid_event && !it.paid_out && it.tickets_sold > 0);
  else if (tab === "to_invoice") view = items.filter((it) => !it.is_paid_event && !it.invoice_paid && it.invoice_amount > 0);
  else if (tab === "history") view = items.filter((it) => it.paid_out || it.invoice_paid);

  return (
    <div className="z-container" data-testid="admin-payouts-page">
      <h1 className="z-page-title">Paiements</h1>
      <p className="z-page-sub">Commission plateforme : <strong>{settings?.commission_pct ?? "—"}%</strong> · Forfait événements gratuits : <strong>{settings?.free_event_fee_tiers?.length ? "par paliers" : formatAmount(settings?.free_event_fee || 0, settings?.free_event_fee_currency || "GNF")}</strong></p>

      <div style={{ display: "flex", gap: 6, marginBottom: 18, flexWrap: "wrap" }}>
        {TABS.map((t) => (
          <button key={t.id} onClick={() => setTab(t.id)} className={`z-chip ${tab === t.id ? "z-chip-active" : ""}`} data-testid={`payout-tab-${t.id}`}>
            {t.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div style={{ textAlign: "center" }}><span className="z-loader z-loader-dark" /></div>
      ) : view.length === 0 ? (
        <EmptyState icon={<Wallet size={28} />} title="Rien à afficher" message="Aucun paiement dans cet onglet." testId="payouts-empty" />
      ) : (
        <div style={{ display: "grid", gap: 12 }}>
          {view.map((it) => {
            const isPayoutLine = it.is_paid_event;
            return (
              <div key={it.event_id} className="z-card" style={{ padding: 16 }} data-testid={`payout-${it.event_id}`}>
                <div style={{ display: "flex", gap: 10, alignItems: "flex-start", flexWrap: "wrap" }}>
                  <div style={{ flex: 1, minWidth: 220 }}>
                    <h3 style={{ margin: "0 0 4px", fontSize: 16, fontWeight: 700 }}>{it.title}</h3>
                    <div style={{ fontSize: 13, color: "var(--z-muted)" }}>par {it.organizer_name} · {formatEventDate(it.starts_at)}</div>
                    <div style={{ marginTop: 8, fontSize: 13 }}>
                      {isPayoutLine ? (
                        <>
                          <div>Billets vendus : <strong>{it.tickets_sold}</strong></div>
                          <div>Recettes brutes : <strong>{formatAmount(it.gross, it.currency)}</strong></div>
                          <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                            {commEdit?.event_id === it.event_id ? (
                              <>
                                <span>Commission :</span>
                                <input
                                  type="number" min="0" max="100" step="0.5"
                                  className="z-input" style={{ width: 80, padding: "4px 8px", margin: 0 }}
                                  value={commEdit.value}
                                  onChange={(e) => setCommEdit({ ...commEdit, value: e.target.value })}
                                  data-testid={`commission-input-${it.event_id}`}
                                />
                                <button className="z-btn z-btn-teal z-btn-sm" disabled={busy} onClick={() => saveCommission(false)} data-testid={`commission-save-${it.event_id}`}>OK</button>
                                <button className="z-btn z-btn-ghost z-btn-sm" onClick={() => saveCommission(true)} data-testid={`commission-reset-${it.event_id}`}>Défaut</button>
                                <button className="z-btn z-btn-ghost z-btn-sm" onClick={() => setCommEdit(null)}>✕</button>
                              </>
                            ) : (
                              <>
                                <span>Commission ({it.commission_pct}%{it.commission_override ? " · personnalisée" : ""}) : <span style={{ color: "var(--z-error)" }}>-{formatAmount(it.commission, it.currency)}</span></span>
                                <button className="z-btn z-btn-ghost z-btn-sm" onClick={() => setCommEdit({ event_id: it.event_id, value: String(it.commission_pct) })} data-testid={`commission-edit-${it.event_id}`}>
                                  <Pencil size={13} />
                                </button>
                              </>
                            )}
                          </div>
                          <div style={{ marginTop: 4, fontWeight: 700, color: "var(--z-success)" }}>
                            Net à reverser : {formatAmount(it.net_payout, it.currency)}
                          </div>
                        </>
                      ) : (
                        <>
                          <div style={{ color: "var(--z-muted)" }}>Événement {it.is_private ? "privé" : "gratuit"} — facturation plateforme</div>
                          <div style={{ marginTop: 4, fontWeight: 700, color: "var(--z-orange)" }}>
                            À facturer : {formatAmount(it.invoice_amount, it.invoice_currency)}
                          </div>
                        </>
                      )}
                    </div>
                    {(it.paid_out || it.invoice_paid) && (
                      <div style={{ marginTop: 8, fontSize: 12, color: "var(--z-success)", display: "flex", alignItems: "center", gap: 4 }}>
                        <CheckCircle2 size={14} /> {it.paid_out ? "Reversé" : "Facturé"} via {it.paid_out ? it.payout?.method : it.invoice?.method} ({it.paid_out ? it.payout?.reference : it.invoice?.reference || "sans réf."})
                      </div>
                    )}
                  </div>
                  <div>
                    {isPayoutLine && !it.paid_out && it.tickets_sold > 0 && (
                      <ZButton variant="teal" size="sm" onClick={() => openPayout(it)} data-testid={`record-payout-${it.event_id}`}>
                        <Wallet size={14} /> Reverser
                      </ZButton>
                    )}
                    {!isPayoutLine && !it.invoice_paid && it.invoice_amount > 0 && (
                      <ZButton variant="primary" size="sm" onClick={() => openInvoice(it)} data-testid={`record-invoice-${it.event_id}`}>
                        <FileText size={14} /> Encaisser
                      </ZButton>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Modal open={!!open} onClose={() => setOpen(null)} title={open?.kind === "payout" ? "Enregistrer un paiement à l'organisateur" : "Enregistrer la facturation"} testId="payout-modal">
        <p style={{ fontSize: 14, color: "var(--z-muted)", marginBottom: 12 }}>
          {open?.item?.title}
        </p>
        <ZInput type="number" label="Montant" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} required data-testid="payout-amount" />
        <ZSelect label="Méthode" value={form.method} onChange={(e) => setForm({ ...form, method: e.target.value })} data-testid="payout-method">
          {(settings?.payout_methods || []).map((m) => <option key={m} value={m}>{m}</option>)}
        </ZSelect>
        <ZInput label="Référence (optionnel)" value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} placeholder="ID transaction / numéro de chèque…" data-testid="payout-reference" />
        <ZTextarea label="Note (optionnel)" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} data-testid="payout-note" />
        {err && <p className="z-error-text">{err}</p>}
        <ZButton onClick={submit} block loading={busy} data-testid="payout-submit">Enregistrer</ZButton>
      </Modal>
    </div>
  );
}
